import React, { useState } from "react";
import styled, { keyframes } from "styled-components";
import {
  Campaign,
  Schedule,
  People,
  CheckCircle,
  AccessTime,
  Delete,
  MoreVert,
  Send,
  Add,
  Search,
  FilterList,
  CalendarMonth,
  Groups,
  TrendingUp,
  Refresh,
  Close,
  Warning,
} from "@mui/icons-material";
import { Dropdown, Modal, Empty } from "antd";
import { format, formatDistanceToNow, isPast, isFuture } from "date-fns";
import {
  primaryActionColor,
  primaryActionColorLight,
  successColor,
  successColorLight,
  warningColor,
  warningColorLight,
  dangerColor,
  dangerColorLight,
  textPrimary,
  textSecondary,
  textMuted,
  borderColor,
  backgroundLight,
} from "../utils/constants";

// Animations
const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(8px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const slideIn = keyframes`
  from {
    opacity: 0;
    transform: translateX(-10px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;

// Layout Components
const PageContainer = styled.div`
  min-height: 100vh;
  background: linear-gradient(135deg, #fafbfc 0%, #f0f4f8 100%);
  padding: 32px 48px;
`;

const PageHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 32px;
  animation: ${fadeIn} 0.4s ease-out;
`;

const HeaderLeft = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const PageTitle = styled.h1`
  font-size: 28px;
  font-weight: 700;
  color: ${textPrimary};
  margin: 0;
  letter-spacing: -0.5px;
  display: flex;
  align-items: center;
  gap: 12px;

  svg {
    color: ${primaryActionColor};
    font-size: 32px;
  }
`;

const PageSubtitle = styled.p`
  font-size: 14px;
  color: ${textSecondary};
  margin: 0;
`;

const CreateButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 24px;
  background: linear-gradient(135deg, ${primaryActionColor} 0%, #4f46e5 100%);
  color: white;
  border: none;
  border-radius: 12px;
  font-size: 14px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 4px 12px rgba(99, 102, 241, 0.3);

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(99, 102, 241, 0.4);
  }

  &:active {
    transform: translateY(0);
  }

  svg {
    font-size: 20px;
  }
`;

// Stats Section
const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 20px;
  margin-bottom: 32px;
`;

const StatCard = styled.div`
  background: white;
  border-radius: 16px;
  padding: 20px 24px;
  border: 1px solid ${borderColor};
  display: flex;
  align-items: center;
  gap: 16px;
  animation: ${fadeIn} 0.4s ease-out;
  animation-delay: ${(props) => props.$delay || "0s"};
  animation-fill-mode: backwards;
  transition: all 0.2s ease;

  &:hover {
    border-color: ${primaryActionColor}20;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
  }
`;

const StatIcon = styled.div`
  width: 48px;
  height: 48px;
  border-radius: 12px;
  background: ${(props) => props.$bg || primaryActionColorLight};
  display: flex;
  align-items: center;
  justify-content: center;

  svg {
    font-size: 24px;
    color: ${(props) => props.$color || primaryActionColor};
  }
`;

const StatContent = styled.div`
  flex: 1;
`;

const StatValue = styled.div`
  font-size: 24px;
  font-weight: 700;
  color: ${textPrimary};
  letter-spacing: -0.5px;
`;

const StatLabel = styled.div`
  font-size: 13px;
  color: ${textMuted};
  margin-top: 2px;
`;

// Toolbar
const Toolbar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 24px;
  gap: 16px;
`;

const SearchContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  background: white;
  border: 1px solid ${borderColor};
  border-radius: 10px;
  padding: 10px 16px;
  width: 320px;
  transition: all 0.2s ease;

  &:focus-within {
    border-color: ${primaryActionColor};
    box-shadow: 0 0 0 3px ${primaryActionColorLight};
  }

  svg {
    color: ${textMuted};
    font-size: 20px;
  }

  input {
    border: none;
    outline: none;
    flex: 1;
    font-size: 14px;
    color: ${textPrimary};

    &::placeholder {
      color: ${textMuted};
    }
  }
`;

const FilterButton = styled.button`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 16px;
  background: white;
  border: 1px solid ${borderColor};
  border-radius: 10px;
  font-size: 14px;
  color: ${textSecondary};
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    border-color: ${primaryActionColor};
    color: ${primaryActionColor};
  }

  svg {
    font-size: 18px;
  }
`;

const ResultCount = styled.span`
  font-size: 14px;
  color: ${textMuted};
`;

// Campaign List
const CampaignList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const CampaignCard = styled.div`
  background: white;
  border-radius: 16px;
  border: 1px solid ${borderColor};
  overflow: hidden;
  animation: ${slideIn} 0.3s ease-out;
  animation-delay: ${(props) => props.$delay || "0s"};
  animation-fill-mode: backwards;
  transition: all 0.2s ease;

  &:hover {
    border-color: ${primaryActionColor}30;
    box-shadow: 0 8px 24px rgba(0, 0, 0, 0.06);
    transform: translateY(-2px);
  }
`;

const CardMain = styled.div`
  display: flex;
  padding: 24px;
  gap: 24px;
`;

const CampaignIcon = styled.div`
  width: 56px;
  height: 56px;
  border-radius: 14px;
  background: ${(props) =>
    props.$status === "sent"
      ? successColorLight
      : props.$status === "scheduled"
      ? primaryActionColorLight
      : warningColorLight};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  svg {
    font-size: 28px;
    color: ${(props) =>
      props.$status === "sent"
        ? successColor
        : props.$status === "scheduled"
        ? primaryActionColor
        : warningColor};
  }
`;

const CampaignContent = styled.div`
  flex: 1;
  min-width: 0;
`;

const CampaignHeader = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 8px;
`;

const CampaignName = styled.h3`
  font-size: 17px;
  font-weight: 600;
  color: ${textPrimary};
  margin: 0;
  letter-spacing: -0.2px;
`;

const CampaignActions = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ActionButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 10px;
  border: 1px solid transparent;
  background: transparent;
  color: ${textMuted};
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background: ${(props) => (props.$danger ? dangerColorLight : backgroundLight)};
    color: ${(props) => (props.$danger ? dangerColor : textSecondary)};
    border-color: ${(props) => (props.$danger ? dangerColor + "30" : borderColor)};
  }

  svg {
    font-size: 20px;
  }
`;

const CampaignMessage = styled.p`
  font-size: 14px;
  color: ${textSecondary};
  margin: 0 0 16px 0;
  line-height: 1.6;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
`;

const CampaignMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 24px;
  flex-wrap: wrap;
`;

const MetaItem = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  color: ${textMuted};

  svg {
    font-size: 16px;
    color: ${(props) => props.$iconColor || textMuted};
  }

  strong {
    color: ${textSecondary};
    font-weight: 500;
  }
`;

const AudienceBadge = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  border-radius: 20px;
  font-size: 12px;
  font-weight: 500;
  background: ${(props) => (props.$exclusive ? dangerColorLight : primaryActionColorLight)};
  color: ${(props) => (props.$exclusive ? dangerColor : primaryActionColor)};

  svg {
    font-size: 14px;
  }
`;

// Status Section (shown separately)
const CardFooter = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 14px 24px;
  background: ${backgroundLight};
  border-top: 1px solid ${borderColor};
`;

const StatusBadge = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  font-weight: 500;
  color: ${(props) =>
    props.$status === "sent"
      ? successColor
      : props.$status === "scheduled"
      ? primaryActionColor
      : warningColor};

  svg {
    font-size: 16px;
  }
`;

const EngagementStats = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
`;

const EngagementItem = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 13px;
  color: ${textSecondary};

  strong {
    font-weight: 600;
    color: ${textPrimary};
  }
`;

// Empty State
const EmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 80px 40px;
  background: white;
  border-radius: 16px;
  border: 1px dashed ${borderColor};
  text-align: center;
`;

const EmptyIcon = styled.div`
  width: 80px;
  height: 80px;
  border-radius: 20px;
  background: ${primaryActionColorLight};
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 24px;

  svg {
    font-size: 40px;
    color: ${primaryActionColor};
  }
`;

const EmptyTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  color: ${textPrimary};
  margin: 0 0 8px 0;
`;

const EmptyDescription = styled.p`
  font-size: 14px;
  color: ${textMuted};
  margin: 0 0 24px 0;
  max-width: 320px;
`;

// Delete Modal
const DeleteModalContent = styled.div`
  text-align: center;
  padding: 20px 0;
`;

const DeleteIcon = styled.div`
  width: 64px;
  height: 64px;
  border-radius: 16px;
  background: ${dangerColorLight};
  display: flex;
  align-items: center;
  justify-content: center;
  margin: 0 auto 20px;

  svg {
    font-size: 32px;
    color: ${dangerColor};
  }
`;

const DeleteTitle = styled.h3`
  font-size: 18px;
  font-weight: 600;
  color: ${textPrimary};
  margin: 0 0 8px 0;
`;

const DeleteDescription = styled.p`
  font-size: 14px;
  color: ${textSecondary};
  margin: 0;
`;

// Sample data
const campaign_data = [
  {
    id: 1,
    name: "Morning Motivation",
    audience: "bodhi_only",
    message:
      "Learn how to set up your first Growezy page by customizing your services, adding your business details, and enabling payment options. Follow our step-by-step guide to create a professional, client-ready website in minutes.",
    status: "sent",
    createdAt: new Date("2026-01-15T09:00:00"),
    scheduledAt: new Date("2026-01-15T10:00:00"),
    sentAt: new Date("2026-01-15T10:00:00"),
    recipients: 1250,
    delivered: 1198,
    opened: 856,
  },
  {
    id: 2,
    name: "New Year Offer - 50% Off",
    audience: "all",
    message:
      "Kick off 2026 with our biggest sale ever! Get 50% off on all premium plans. Limited time offer - upgrade your business presence today and watch your client base grow exponentially.",
    status: "scheduled",
    createdAt: new Date("2026-01-17T14:30:00"),
    scheduledAt: new Date("2026-01-20T09:00:00"),
    recipients: 3420,
    delivered: 0,
    opened: 0,
  },
  {
    id: 3,
    name: "Feature Update Announcement",
    audience: "all",
    message:
      "Exciting news! We've just launched powerful new features including automated invoicing, advanced analytics dashboard, and seamless calendar integrations. Update your app to explore these game-changing tools.",
    status: "draft",
    createdAt: new Date("2026-01-18T11:00:00"),
    scheduledAt: null,
    recipients: 0,
    delivered: 0,
    opened: 0,
  },
  {
    id: 4,
    name: "Weekly Tips & Tricks",
    audience: "all",
    message:
      "This week's tip: Use our scheduling feature to automate your availability. Clients can book directly, reducing back-and-forth messages by 80%. Try it now!",
    status: "sent",
    createdAt: new Date("2026-01-10T08:00:00"),
    scheduledAt: new Date("2026-01-12T08:00:00"),
    sentAt: new Date("2026-01-12T08:00:00"),
    recipients: 2890,
    delivered: 2756,
    opened: 1834,
  },
];

function Broadcast() {
  const [campaigns, setCampaigns] = useState(campaign_data);
  const [searchQuery, setSearchQuery] = useState("");
  const [deleteModal, setDeleteModal] = useState({ open: false, campaign: null });

  const filteredCampaigns = campaigns.filter(
    (c) =>
      c.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      c.message.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const stats = {
    total: campaigns.length,
    sent: campaigns.filter((c) => c.status === "sent").length,
    scheduled: campaigns.filter((c) => c.status === "scheduled").length,
    totalRecipients: campaigns.reduce((sum, c) => sum + c.recipients, 0),
  };

  const handleDelete = (campaign) => {
    setDeleteModal({ open: true, campaign });
  };

  const confirmDelete = () => {
    if (deleteModal.campaign) {
      setCampaigns((prev) => prev.filter((c) => c.id !== deleteModal.campaign.id));
    }
    setDeleteModal({ open: false, campaign: null });
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case "sent":
        return <CheckCircle />;
      case "scheduled":
        return <Schedule />;
      default:
        return <AccessTime />;
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case "sent":
        return "Sent";
      case "scheduled":
        return "Scheduled";
      default:
        return "Draft";
    }
  };

  const getCampaignIcon = (status) => {
    switch (status) {
      case "sent":
        return <CheckCircle />;
      case "scheduled":
        return <Schedule />;
      default:
        return <Campaign />;
    }
  };

  const getMenuItems = (campaign) => [
    {
      key: "resend",
      label: "Send Again",
      icon: <Refresh style={{ fontSize: 16 }} />,
      disabled: campaign.status !== "sent",
    },
    {
      key: "edit",
      label: "Edit Campaign",
      icon: <Campaign style={{ fontSize: 16 }} />,
    },
    { type: "divider" },
    {
      key: "delete",
      label: "Delete",
      icon: <Delete style={{ fontSize: 16 }} />,
      danger: true,
      onClick: () => handleDelete(campaign),
    },
  ];

  return (
    <PageContainer>
      <PageHeader>
        <HeaderLeft>
          <PageTitle>
            <Campaign />
            Broadcast Campaigns
          </PageTitle>
          <PageSubtitle>
            Create and manage your broadcast messages to reach your audience
          </PageSubtitle>
        </HeaderLeft>
        <CreateButton>
          <Add />
          New Campaign
        </CreateButton>
      </PageHeader>

      <StatsGrid>
        <StatCard $delay="0s">
          <StatIcon $bg={primaryActionColorLight} $color={primaryActionColor}>
            <Campaign />
          </StatIcon>
          <StatContent>
            <StatValue>{stats.total}</StatValue>
            <StatLabel>Total Campaigns</StatLabel>
          </StatContent>
        </StatCard>

        <StatCard $delay="0.05s">
          <StatIcon $bg={successColorLight} $color={successColor}>
            <CheckCircle />
          </StatIcon>
          <StatContent>
            <StatValue>{stats.sent}</StatValue>
            <StatLabel>Sent</StatLabel>
          </StatContent>
        </StatCard>

        <StatCard $delay="0.1s">
          <StatIcon $bg={warningColorLight} $color={warningColor}>
            <Schedule />
          </StatIcon>
          <StatContent>
            <StatValue>{stats.scheduled}</StatValue>
            <StatLabel>Scheduled</StatLabel>
          </StatContent>
        </StatCard>

        <StatCard $delay="0.15s">
          <StatIcon $bg="#e0f2fe" $color="#0284c7">
            <Groups />
          </StatIcon>
          <StatContent>
            <StatValue>{stats.totalRecipients.toLocaleString()}</StatValue>
            <StatLabel>Total Reach</StatLabel>
          </StatContent>
        </StatCard>
      </StatsGrid>

      <Toolbar>
        <SearchContainer>
          <Search />
          <input
            type="text"
            placeholder="Search campaigns..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </SearchContainer>
        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
          <ResultCount>
            {filteredCampaigns.length} campaign{filteredCampaigns.length !== 1 && "s"}
          </ResultCount>
          <FilterButton>
            <FilterList />
            Filter
          </FilterButton>
        </div>
      </Toolbar>

      {filteredCampaigns.length === 0 ? (
        <EmptyState>
          <EmptyIcon>
            <Campaign />
          </EmptyIcon>
          <EmptyTitle>No campaigns found</EmptyTitle>
          <EmptyDescription>
            {searchQuery
              ? "Try adjusting your search to find what you're looking for."
              : "Create your first broadcast campaign to reach your audience."}
          </EmptyDescription>
          {!searchQuery && (
            <CreateButton>
              <Add />
              Create Campaign
            </CreateButton>
          )}
        </EmptyState>
      ) : (
        <CampaignList>
          {filteredCampaigns.map((campaign, index) => (
            <CampaignCard key={campaign.id} $delay={`${index * 0.05}s`}>
              <CardMain>
                <CampaignIcon $status={campaign.status}>
                  {getCampaignIcon(campaign.status)}
                </CampaignIcon>

                <CampaignContent>
                  <CampaignHeader>
                    <CampaignName>{campaign.name}</CampaignName>
                    <CampaignActions>
                      <ActionButton $danger onClick={() => handleDelete(campaign)}>
                        <Delete />
                      </ActionButton>
                      <Dropdown
                        menu={{ items: getMenuItems(campaign) }}
                        trigger={["click"]}
                        placement="bottomRight"
                      >
                        <ActionButton>
                          <MoreVert />
                        </ActionButton>
                      </Dropdown>
                    </CampaignActions>
                  </CampaignHeader>

                  <CampaignMessage>{campaign.message}</CampaignMessage>

                  <CampaignMeta>
                    <AudienceBadge $exclusive={campaign.audience === "bodhi_only"}>
                      <People />
                      {campaign.audience === "bodhi_only" ? "Bodhi Users Only" : "All Users"}
                    </AudienceBadge>

                    {campaign.scheduledAt && (
                      <MetaItem $iconColor={primaryActionColor}>
                        <CalendarMonth />
                        <strong>
                          {campaign.status === "sent" ? "Sent" : "Scheduled"}:
                        </strong>
                        {format(campaign.scheduledAt, "MMM d, yyyy 'at' h:mm a")}
                      </MetaItem>
                    )}

                    {campaign.recipients > 0 && (
                      <MetaItem>
                        <Groups />
                        <strong>{campaign.recipients.toLocaleString()}</strong> recipients
                      </MetaItem>
                    )}
                  </CampaignMeta>
                </CampaignContent>
              </CardMain>

              <CardFooter>
                <StatusBadge $status={campaign.status}>
                  {getStatusIcon(campaign.status)}
                  {getStatusLabel(campaign.status)}
                  {campaign.status === "scheduled" && campaign.scheduledAt && (
                    <span style={{ fontWeight: 400, marginLeft: 4 }}>
                      • {formatDistanceToNow(campaign.scheduledAt, { addSuffix: true })}
                    </span>
                  )}
                  {campaign.status === "sent" && campaign.sentAt && (
                    <span style={{ fontWeight: 400, marginLeft: 4 }}>
                      • {formatDistanceToNow(campaign.sentAt, { addSuffix: true })}
                    </span>
                  )}
                </StatusBadge>

                {campaign.status === "sent" && (
                  <EngagementStats>
                    <EngagementItem>
                      <TrendingUp style={{ fontSize: 16, color: successColor }} />
                      <strong>{campaign.delivered.toLocaleString()}</strong> delivered
                    </EngagementItem>
                    <EngagementItem>
                      <strong>
                        {campaign.delivered > 0
                          ? Math.round((campaign.opened / campaign.delivered) * 100)
                          : 0}
                        %
                      </strong>{" "}
                      open rate
                    </EngagementItem>
                  </EngagementStats>
                )}

                {campaign.status === "draft" && (
                  <CreateButton
                    style={{
                      padding: "8px 16px",
                      fontSize: 13,
                      boxShadow: "none",
                    }}
                  >
                    <Send style={{ fontSize: 16 }} />
                    Schedule
                  </CreateButton>
                )}
              </CardFooter>
            </CampaignCard>
          ))}
        </CampaignList>
      )}

      {/* Delete Confirmation Modal */}
      <Modal
        open={deleteModal.open}
        onCancel={() => setDeleteModal({ open: false, campaign: null })}
        footer={null}
        centered
        width={400}
        closable={false}
      >
        <DeleteModalContent>
          <DeleteIcon>
            <Warning />
          </DeleteIcon>
          <DeleteTitle>Delete Campaign?</DeleteTitle>
          <DeleteDescription>
            Are you sure you want to delete "{deleteModal.campaign?.name}"? This action cannot be
            undone.
          </DeleteDescription>
          <div
            style={{
              display: "flex",
              gap: 12,
              justifyContent: "center",
              marginTop: 24,
            }}
          >
            <FilterButton
              onClick={() => setDeleteModal({ open: false, campaign: null })}
              style={{ padding: "10px 24px" }}
            >
              Cancel
            </FilterButton>
            <CreateButton
              onClick={confirmDelete}
              style={{
                background: dangerColor,
                boxShadow: `0 4px 12px ${dangerColor}40`,
              }}
            >
              <Delete style={{ fontSize: 18 }} />
              Delete
            </CreateButton>
          </div>
        </DeleteModalContent>
      </Modal>
    </PageContainer>
  );
}

export default Broadcast;
